import { useState } from "react";
import { Alert, StyleSheet, View } from "react-native";

import { AppText, Button, ButtonText, Stack, TextField } from "~/components/ui";
import { spacing } from "~/styles/theme";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";

export function CollectionEmailShare({
  collectionId,
}: {
  collectionId: string;
}) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function sendShare() {
    setError(null);
    setBusy(true);
    try {
      const data = await unwrap(
        api.shareCollection({
          body: {
            collection_id: collectionId,
            email: email.trim(),
            message: message.trim() || undefined,
          },
        }),
      );
      setEmail("");
      setMessage("");
      if (!data.email_sent) {
        Alert.alert(
          "Invite saved",
          "The invite was saved, but the email could not be sent.",
        );
      } else {
        Alert.alert("Shared", "The collection link has been emailed.");
      }
    } catch (shareError) {
      setError(
        shareError instanceof Error
          ? shareError.message
          : "Unable to send that share link.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap="sm">
      <AppText size="xs">
        Email a private share link. Private collections become unlisted when you
        first share them.
      </AppText>
      <View style={stylesheet.field}>
        <AppText size="xs" weight="semibold">
          Email
        </AppText>
        <TextField
          accessibilityLabel="Share recipient email"
          autoCapitalize="none"
          keyboardType="email-address"
          value={email}
          placeholder="friend@example.com"
          onChangeText={setEmail}
        />
      </View>
      <View style={stylesheet.field}>
        <AppText size="xs" weight="semibold">
          Message (optional)
        </AppText>
        <TextField
          accessibilityLabel="Share message"
          value={message}
          placeholder="Come see what I've been growing"
          multiline
          style={stylesheet.textarea}
          onChangeText={setMessage}
        />
      </View>
      {error ? (
        <AppText size="xs" tone="danger">
          {error}
        </AppText>
      ) : null}
      <Button
        align="start"
        iconName="mail"
        disabled={busy || !email.trim()}
        onPress={() => void sendShare()}
      >
        <ButtonText>{busy ? "Sending..." : "Email share link"}</ButtonText>
      </Button>
    </Stack>
  );
}

const stylesheet = StyleSheet.create({
  field: { gap: spacing.xs },
  textarea: { minHeight: 72, textAlignVertical: "top" },
});
